import React from 'react';
import { Shell as ShellProvider } from '@vtblife/layout-components';
import type { ConfigContext } from '@vtblife/layout-components/dist/shell/contexts';

import { useGql2Loader } from '@search/gql-client/src/useGql2';
import { graphql, useGqlContext } from '@search/gql-client/src';
import { useM2AuthContext } from '@search/auth/src/M2AuthProvider';
import { AuthInfoUser } from '@search/auth/src/AuthInfo';

import { ClShellProviderQuery$data, ClShellProviderQuery$variables } from './__generated__/ClShellProviderQuery.graphql';

// Костыль, иначе падет классифайд, из-за ошибки внутри layout-components
const BLOCK_PERMISSIONS = [
    // 'alternative-deal',
    // 'auth',
    // 'bank-order',
    // 'billing',
    // 'chat',
    // 'classified',
    // 'client-sign',
    // 'client-verification',
    // 'clients',
    // 'company',
    // 'contract-kit',
    // 'deal',
    // 'deal-bulk-manager',
    // 'deal-calendar',
    // 'deal-controller',
    // 'deal-online',
    // 'deal-sign',
    // 'ereg-payments',
    // 'eregistration',
    // 'escrow',
    'helpdesk',
    // 'm2pro',
    // 'market',
    // 'mortgage',
    // 'person',
    // 'real-estate',
    // 'rosreestr-data',
    // 'sbr',
    // 'sign-store',
    // 'tariff'
];

function userWithoutPermissions(user: AuthInfoUser) {
    return {
        ...user,
        permissions: user?.permissions.filter(s =>
            ! BLOCK_PERMISSIONS.some(blocked => s.startsWith(`/${blocked}/`))
        )
    };
}

const clShellProviderQuery = graphql`
    query ClShellProviderQuery($regionId: Int) {
        shellConfig2(regionId: $regionId) {
            cdnUrl
            supportPhone
            backupSupportPhone
            copyright
            ITRequirements
            yandexCaptcha {
                clientKey
            }
            features {
                name
            }
            mortgageABTest
            header {
                items {
                    id
                    displayName
                    displayNameMobile
                    device { desktop mobile }
                    exact
                    target
                    path
                    icon
                    dropdownMenu {
                        id
                        displayName
                        displayNameMobile
                        device { desktop mobile }
                        exact
                        target
                        url
                        icon
                        dropdownMenu {
                            id
                            displayName
                            displayNameMobile
                            device { desktop mobile }
                            exact
                            target
                            url
                        }
                    }
                }
                partners {
                    samolet {
                        id
                        logo
                        logoWhite
                        url
                    }
                }
                personalButtons {
                    favorites {
                        professionalUrl,
                        url
                    }
                    offerPlacement {
                        professionalUrl,
                        authorizedUrl,
                        url
                    }
                    savedSearch {
                        professionalUrl,
                        url
                    }
                }
                loginPaths {
                    default {
                        id
                        url
                    }
                    client {
                        id
                        url
                    }
                    partner {
                        id
                        url
                    }
                    professional {
                        id
                        url
                    }
                }
            }
            sidebar {
                items {
                    id
                    displayName
                    path
                    url
                    icon
                    showInDesktopProfileMenu
                    dropdownMenu {
                        id
                        displayName
                        path
                        url
                        icon
                        showInDesktopProfileMenu
                    }
                }
                footerItems {
                    id
                    displayName
                    path
                    url
                    icon
                    showInDesktopProfileMenu
                }
            }
            footer {
                downloadAppQrCode
                socialLinks {
                    name
                    icon
                    href
                }
                apps {
                    href
                    imgSrc
                    analytics
                }
                top {
                    to
                    name
                    href
                    cdn
                    analytics
                    target
                }
                middleLinks {
                    title
                    links {
                        to
                        href
                        cdn
                        name
                        analytics
                        target
                        linkGenerationId
                    }
                }
            }
        }
    }
`;

const defaultShellConfig: Readonly<Partial<ClShellConfigContext>> = { cdnUrl: 'https://cdn.m2.ru' };

function useShellFetcher() {
    const gql = useGqlContext();

    return React.useCallback(
        (vars: ClShellProviderQuery$variables, opts?: { signal?: AbortSignal }) => gql.client<
            ClShellProviderQuery$variables,
            ClShellProviderQuery$data
        >(clShellProviderQuery, vars, opts)
        , [ gql ]
    );
}

const CustomConfigContext = React.createContext<Readonly<Partial<ClShellConfigContext>>>(defaultShellConfig);

function CustomConfigProvider({ config, children }: {
    children?: React.ReactNode;
    config: Readonly<Partial<ClShellConfigContext>>;
}) {
    return (
        <CustomConfigContext.Provider value={config}>
            {children}
        </CustomConfigContext.Provider>
    );
}

export const useCustomConfig = () => {
    return React.useContext(CustomConfigContext);
};

export function useShellLoader() {
    return useGql2Loader(useShellFetcher(), { cachePrefix: 'Shell', authTrack: true });
}

export type ClShellConfigContext = ConfigContext & Readonly<NonNullable<ClShellProviderQuery$data['shellConfig2']>>;

export function ClShellProvider({
    children,
    promoRegion
}: {
    children?: React.ReactNode;
    promoRegion?: number;
}) {
    const shellLoader = useShellLoader();
    const { data } = shellLoader({ regionId: promoRegion });
    const { auth: { user } } = useM2AuthContext();

    const modUser = user?.currentRoleType === 'EMPLOYEE' ?
        userWithoutPermissions(user) : user;

    const dataRef = React.useRef<Readonly<Partial<ClShellConfigContext>>>(defaultShellConfig);

    // @ts-expect-error: relay-readonly GQL-форма не присваивается в Partial по variance, но runtime - тот же объект.
    if (data?.shellConfig2) dataRef.current = data.shellConfig2;

    return (
        <CustomConfigProvider config={dataRef.current}>
            <ShellProvider
                shouldAskCookieAgreement
                user={modUser ? { ...modUser, companyId: modUser.companyId ?? null } : null}
                config={dataRef.current}
                hasNavbarOnMobile={false}
            >
                {children}
            </ShellProvider>
        </CustomConfigProvider>
    );
}
